import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tooltip } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRow, listUsers, toggleUserStatus } from '../api/users';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    default:
      return 'default';
  }
};

export const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: listUsers,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<string>('all');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'name' | 'email' | 'phone' | 'role' | 'status' | 'createdAt' | 'updatedAt'
  >('name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');

  const handleRequestSort = (
    property:
      | 'name'
      | 'email'
      | 'phone'
      | 'role'
      | 'status'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((u) => {
      const haystack = `${u.firstName ?? ''} ${u.lastName ?? ''} ${
        u.email ?? ''
      } ${u.phone ?? ''}`.toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesRole =
        roleFilter === 'all' || (u.role ?? 'UNKNOWN') === roleFilter;
      const matchesStatus =
        statusFilter === 'all' || (u.status ?? 'active') === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [data, search, roleFilter, statusFilter]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      const getName = (u: UserRow) =>
        `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim().toLowerCase();
      let av: string | number | Date | undefined;
      let bv: string | number | Date | undefined;

      switch (orderBy) {
        case 'name':
          av = getName(a);
          bv = getName(b);
          break;
        case 'email':
          av = (a.email ?? '').toLowerCase();
          bv = (b.email ?? '').toLowerCase();
          break;
        case 'phone':
          av = (a.phone ?? '').toLowerCase();
          bv = (b.phone ?? '').toLowerCase();
          break;
        case 'role':
          av = (a.role ?? '').toString().toLowerCase();
          bv = (b.role ?? '').toString().toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toString().toLowerCase();
          bv = (b.status ?? '').toString().toLowerCase();
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0;
          bv = 0;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleToggleStatus = async (user: UserRow) => {
    await toggleUserStatus(user.id, user.status);
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Utilisateurs
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Nom, email, téléphone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="role-filter-label">Rôle</InputLabel>
          <Select
            labelId="role-filter-label"
            label="Rôle"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="responsible">Responsable</MenuItem>
            <MenuItem value="teacher">Enseignant</MenuItem>
            <MenuItem value="cgs">CGS</MenuItem>
            <MenuItem value="drh">DRH</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="active">Actif</MenuItem>
            <MenuItem value="inactive">Inactif</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Nom
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'phone' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'phone'}
                  direction={orderBy === 'phone' ? order : 'asc'}
                  onClick={() => handleRequestSort('phone')}
                >
                  Téléphone
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'role' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Rôle
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    {u.lastName ?? ''} {u.firstName ?? ''}
                  </TableCell>
                  <TableCell>{u.email ?? '—'}</TableCell>
                  <TableCell>{u.phone ?? '—'}</TableCell>
                  <TableCell>{u.role ?? 'UNKNOWN'}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.status ?? 'active'}
                      color={statusColor(u.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(u.createdAt)}</TableCell>
                  <TableCell>{formatDate(u.updatedAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        u.status === 'active'
                          ? 'Désactiver cet utilisateur'
                          : 'Activer cet utilisateur'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(u)}
                      >
                        {u.status === 'active' ? (
                          <BlockIcon fontSize="small" />
                        ) : (
                          <CheckCircleIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun utilisateur trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
};

